import { DEFAULT_CONNECTOR_FORM } from "./constants";
import type {
  CreateMCPConnectionPayload,
  MCPConnectionDTO,
  MCPSharing,
  MCPSharingUserItem,
  MCPTransport,
  MCPUserRole,
} from "../../services/agent-tools-service";
import type {
  AccessLevel,
  AuthHeader,
  ConnectorFormValues,
  MCPConnection,
  TransportProtocol,
} from "./types";

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "numeric",
};

const safeHostname = (endpoint: string): string => {
  try {
    return new URL(endpoint).hostname;
  } catch {
    return endpoint;
  }
};

const formatTimestamp = (iso: string, locale: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, DATE_FORMAT);
};

export const DEFAULT_USER_ROLE: MCPUserRole = "viewer";

// Sharing user items come in two shapes: bare id strings (legacy) or
// { id, role } objects. Normalize both into objects so the UI has one model.
export const normalizeSharingUsers = (
  items: Array<string | MCPSharingUserItem> | undefined | null,
): MCPSharingUserItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item): MCPSharingUserItem | null => {
      if (typeof item === "string") {
        return item ? { id: item, role: DEFAULT_USER_ROLE } : null;
      }
      if (item && typeof item.id === "string") {
        return { id: item.id, role: item.role ?? DEFAULT_USER_ROLE };
      }
      return null;
    })
    .filter((item): item is MCPSharingUserItem => item !== null);
};

// Count how many recipients the connection is shared with so the drawer can
// show e.g. "Selected user(s) 5" without an extra request. Defensive against
// missing/odd-shaped sharing payloads — older rows can come back without one.
const countSharingTargets = (dto: MCPConnectionDTO): number => {
  const sharing = dto.sharing as MCPSharing | undefined | null;
  if (!sharing) return 0;
  if (sharing.scope === "project") {
    return "item" in sharing && sharing.item ? 1 : 0;
  }
  if (sharing.scope === "user") {
    return "items" in sharing && Array.isArray(sharing.items)
      ? sharing.items.length
      : 0;
  }
  return 0;
};

// Form values use a slightly different shape than the API payload (separate
// fields per auth type, an `accessLevel` enum vs. `sharing.scope`), so we
// translate here rather than leaking API field names into the form layer.
export const mapFormToCreatePayload = (
  values: ConnectorFormValues,
): CreateMCPConnectionPayload => {
  if (!values.transport) {
    throw new Error("Transport protocol is required");
  }

  const sharing: MCPSharing =
    values.accessLevel === "project"
      ? { scope: "project", item: values.projectId }
      : values.accessLevel === "user"
        ? { scope: "user", items: values.users }
        : { scope: "organization" };

  const payload: CreateMCPConnectionPayload = {
    alias: values.alias.trim(),
    endpoint: values.serverUrl.trim(),
    transport: values.transport as MCPTransport,
    auth_type: values.authHeader,
    is_active: true,
    sharing,
  };

  if (values.authHeader === "oauth") {
    payload.oauth_client_mode = "dcr";
    if (values.oauthClientId) payload.auth_client_id = values.oauthClientId;
    if (values.oauthClientSecret) {
      payload.auth_client_secret = values.oauthClientSecret;
    }
  }

  if (values.authHeader === "api_key" && values.apiKey) {
    payload.auth_token = values.apiKey;
  }

  if (values.authHeader === "bearer" && values.bearerToken) {
    payload.auth_token = values.bearerToken;
  }

  return payload;
};

// Rows created before the auth type was spelled `api_key` come back with the
// hyphenated value; map it onto the current one so the edit dialog still shows
// the API key field for them.
const normalizeAuthHeader = (authType: string | null | undefined): AuthHeader =>
  authType === "api-key" ? "api_key" : ((authType ?? "none") as AuthHeader);

// Reverse of mapFormToCreatePayload — used to seed the edit dialog. Secrets
// (oauth_client_secret, auth_token) are not returned by the API for security
// reasons, so those fields start blank and the user only re-enters them if
// they want to rotate the credential.
//
// Defensive against partial/loosely-typed responses: any missing or unknown
// field falls back to a safe form default so this function NEVER throws.
// A throw here would surface during render in the page's `useMemo`, crashing
// the page and (when the parent state still points at the broken connection)
// causing a re-render → re-throw cycle.
export const mapDtoToFormValues = (
  dto: MCPConnectionDTO,
): ConnectorFormValues => {
  const sharing = (dto.sharing ?? { scope: "organization" }) as
    | MCPSharing
    | { scope?: string };

  const scope: AccessLevel =
    sharing.scope === "project" || sharing.scope === "user"
      ? sharing.scope
      : "organization";

  const projectId =
    scope === "project" && "item" in sharing && typeof sharing.item === "string"
      ? sharing.item
      : "";

  const users =
    scope === "user" && "items" in sharing
      ? normalizeSharingUsers(sharing.items as Array<string | MCPSharingUserItem>)
      : [];

  return {
    ...DEFAULT_CONNECTOR_FORM,
    alias: dto.alias ?? "",
    serverUrl: dto.endpoint ?? "",
    transport: (dto.transport ?? "") as TransportProtocol | "",
    authHeader: normalizeAuthHeader(dto.auth_type),
    oauthClientId: dto.auth_client_id ?? "",
    accessLevel: scope,
    projectId,
    users,
  };
};

// Prefer the user-provided alias for display; fall back to the endpoint
// hostname for legacy rows created before alias was a field.
export const mapConnectionDtoToUi = (
  dto: MCPConnectionDTO,
  locale: string,
): MCPConnection => {
  const displayName = dto.alias?.trim() || safeHostname(dto.endpoint);
  return {
    id: dto.id,
    name: displayName,
    icon: displayName.charAt(0).toUpperCase(),
    iconUrl: dto.icon_url ?? null,
    iconClassName: undefined,
    createdAt: formatTimestamp(dto.created_at, locale),
    status: dto.is_active ? "online" : "offline",
    description: dto.description,
    selectedUsers: countSharingTargets(dto),
  };
};
